import React from 'react';
import Modal, { ModalProps } from '@ax2/common/components/Modal';
import styles from 'styles/components/ScreenersLibraryModalData.module.css';
import _ from 'lodash';
import { ActionButton } from '@ax2/common/components/ActionButton';
import plusIcon from '../assets/plus_blue.svg';
import UUID from '@ax2/common/model/UUID';
import {
  useSurveyContext,
  useSurveyDispatchContext,
} from 'contexts/SurveyContext';
import { fixOrder } from '@ax2/common/helpers/array';
import { ExpandableQuestionsFlow } from './expandable/ExpandableQuestionsFlow';
import { isEditable } from 'helpers/question';
import { createQuestionFromTemplate } from 'rest/makeSurveyClient';
import { getMockedScreeners } from 'rest/mockedDataClient';
import ScreenerQuestionSchema from '@ax2/common/model/schemas/ScreenerQuestionSchema';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';
import { saveIcon } from 'rest/shopClient';

type ScreenersLibraryModalData = ModalProps;

const modalScrollableContentWrapperId = 'slm-modal';

export const ScreenersLibraryModal: React.FC<
  ScreenersLibraryModalData
> = props => {
  const surveySchema = useSurveyContext();
  const surveyDispatch = useSurveyDispatchContext();
  const updateError = useErrorUpdateContext();
  const handleNewScreener = (question: ScreenerQuestionSchema) => {
    if (surveySchema && surveySchema.screeners.length < 2) {
      createQuestionFromTemplate(surveySchema.id, question.id)
        .then(id => {
          surveyDispatch({
            type: 'screeners',
            screeners: fixOrder([
              ...surveySchema.screeners,
              { ...question, id: id, questionTemplateId: question.id },
            ]),
          });
        })
        .catch(e =>
          updateError({
            text: getErrorMessage(e as RestError),
          })
        );
    }
  };
  const isQuestionSelected = (questionId: UUID) => {
    return (
      _.findIndex(
        surveySchema ? surveySchema.screeners : [],
        s => s.questionTemplateId === questionId
      ) !== -1
    );
  };
  const templates = surveySchema
    ? _.filter(
        getMockedScreeners(),
        s =>
          _.findIndex(s.allowedPanels, ap => ap === surveySchema.panel) !== -1
      )
    : [];
  return (
    <Modal
      {...props}
      scrollableContentWrapperId={modalScrollableContentWrapperId}
    >
      <div className={styles.sLibContainer}>
        <span className={styles.title}>Screener Questions Library</span>
        <span className={styles.subtitle}>
          You may add up to 2 screener questions
        </span>
        <div className={styles.screenersTemplatesList}>
          {_.map(templates, (s, index) => (
            <div key={index} className={styles.screenerElement}>
              <ExpandableQuestionsFlow
                id={'slm-screener-' + (index + 1)}
                containerId={modalScrollableContentWrapperId}
                title={`Screener ${index + 1}`}
                subtitle={isEditable(s) ? 'Editable' : 'Non-Editable'}
                expandLabel="Preview this screener"
                previewContent={
                  <ScreenerPurposeElement
                    title={s.purposeTitle}
                    text={s.purposeText}
                  />
                }
                actionElement={
                  !isQuestionSelected(s.id) ? (
                    <ActionButton
                      text="Add"
                      type="secondary"
                      icon={{
                        source: plusIcon,
                        size: 'xnormal',
                        position: 'left',
                      }}
                      onClick={() => {
                        if (surveySchema) {
                          saveIcon(`color1+${surveySchema.id}`, 'false');
                          saveIcon(`color3+${surveySchema.id}`, 'false');
                          saveIcon(`color2+${surveySchema.id}`, 'false');
                        }
                        handleNewScreener(s);
                      }}
                    />
                  ) : (
                    <ActionButton
                      text="Added!"
                      type="secondaryAlt"
                      onClick={() => {}}
                    />
                  )
                }
                screeners={[s]}
                questions={[]}
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

interface ScreenerPurposeElementData {
  title: string;
  text: string;
}

const ScreenerPurposeElement: React.FC<ScreenerPurposeElementData> = props => {
  return (
    <div className={styles.info}>
      <span className={styles.purposeTitle}>{props.title}</span>
      <span className={styles.purposeText}>{props.text}</span>
    </div>
  );
};
